import { useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Box, DataGrid, Grid, IconButton, Tooltip, Typography } from "../../../components";
import {
  formatCelular,
  formatCnpj,
  formatCpf,
  formatTelefone,
} from "../../../utils";
import { InformacoesPessoasDialog } from "./informacoes-pessoas-dialog.component";
import { tiposPessoaEnum } from "../constantes";

const ImportarPessoasStepper = ({
  activeStep,
  arquivoCsv,
  setArquivoCsv,
  dadosCsv,
  tiposPessoa,
  estados,
  inputErros,
  setLoading,
  enderecoService,
  cacheDto,
  setCacheDto
}) => {
  const [pessoaSelecionada, setPessoaSelecionada] = useState(null);
  const [linhaSelecionada, setLinhaSelecionada] = useState(null);
  const [openInformacoesDialog, setOpenInformacoesDialog] = useState(false);
  const [abaValue, setAbaValue] = useState(0);
  const csvUpload = useRef(null);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: [
      "text/csv",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ],
    onDrop: (acceptedFiles) => {
      handleCsvChange(acceptedFiles);
    },
  });

  function handleCsvChange(event) {
    if (event.target) {
      setArquivoCsv(event.target.files[0]);
    } else {
      setArquivoCsv(event[0]);
    }
  }

  const colunasProdutos = [
    {
      field: "naturezaSocial",
      headerName: "Natureza Social",
      sortable: false,
      width: 150,
      valueGetter: (params) =>
        params.value?.charAt(0).toUpperCase() + params.value?.slice(1),
      renderCell: (params) => renderCellTratamentoErros(params),
    },
    {
      field: "nomeRazaoSocial",
      headerName: "Nome / Razão Social",
      sortable: false,
      width: 350,
      renderCell: (params) => renderCellTratamentoErros(params),
    },
    {
      field: "apelidoFantasia",
      headerName: "Apelido Fantasia",
      sortable: false,
      width: 200,
    },
    {
      field: "dataNascimento",
      headerName: "Data de Nascimento / Abertura",
      sortable: false,
      width: 150,
      valueGetter: (params) =>
        params.row.naturezaSocial === "fisica"
          ? params.row.dataNascimento?.toLocaleDateString("pt-BR")
          : params.row.dataAbertura?.toLocaleDateString("pt-BR"),
      renderCell: (params) => renderCellTratamentoErros(params),
    },
    {
      field: "cnpjCpf",
      headerName: "CNPJ / CPF",
      sortable: false,
      width: 200,
      valueGetter: (params) =>
        params.row.naturezaSocial === "fisica"
          ? formatCpf(params.value)
          : formatCnpj(params.value),
      renderCell: (params) => renderCellTratamentoErros(params),
    },
    {
      field: "inscricaoEstadual",
      headerName: "Inscrição Estadual",
      sortable: false,
      width: 200,
    },
    {
      field: "inscricaoEstadualRural",
      headerName: "Inscrição Estadual Rural",
      sortable: false,
      width: 200,
    },
    {
      field: "rg",
      headerName: "RG",
      sortable: false,
      width: 150,
    },
    {
      field: "contato",
      headerName: "Contato",
      sortable: false,
      width: 150,
    },
    {
      field: "email",
      headerName: "Email",
      sortable: false,
      width: 200,
    },
    {
      field: "telefone",
      headerName: "Telefone",
      sortable: false,
      width: 200,
      valueGetter: (params) => formatTelefone(params.value),
      renderCell: (params) => renderCellTratamentoErros(params),
    },
    {
      field: "celular",
      headerName: "Celular",
      sortable: false,
      width: 200,
      valueGetter: (params) => formatCelular(params.value),
      renderCell: (params) => renderCellTratamentoErros(params),
    },
    {
      headerName: "Ações",
      sortable: false,
      width: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const rowIndex = dadosCsv.findIndex(({ id }) => params.id === id);
        return (
          params.row.idTiposPessoa.length &&
            <IconButton
            onClick={() => handleOpenInformacoesDialog(params.row, rowIndex)}
            sx={{
              "&:hover": { backgroundColor: "transparent" },
            }}
          >
          <Tooltip title="Informações Adicionais" arrow placement="left">
            <i
              className="ph-fill ph-eye"
              style={{ fontSize: 20, marginLeft: 14 }}
            ></i>
          </Tooltip>
          </IconButton>
        );
      },
    }
  ];

  const handleOpenInformacoesDialog = (pessoa, rowIndex) => {
    const tipoPessoa = pessoa.idTiposPessoa[0];
    if (!pessoa.endereco && !tipoPessoa) {
      return;
    } else {
      if (pessoa.endereco) {
        setAbaValue(0);
      } else if (tipoPessoa) {
        const descricao = tiposPessoa.find(({ id }) => id === tipoPessoa)?.descricao;
        const format = descricao.split(" ")[0];
        setAbaValue(tiposPessoaEnum[format] + 1);
      }
      setLinhaSelecionada(rowIndex);
      setPessoaSelecionada(pessoa);
      setOpenInformacoesDialog(true);
    }
  }

  function erroCelulaGridPessoas(params, inputErros) {
    if (!inputErros || !params) return "";
    const indiceItem = params.api.getRowIndexRelativeToVisibleRows(
      params.row.id,
    );
    return inputErros.get(`[${indiceItem}].${params.field}`);
  }

  const renderCellTratamentoErros = (params) => {
    const cenarioErro = erroCelulaGridPessoas(params, inputErros);
    const content = cenarioErro ? (
      <Tooltip
        title={`${params.colDef.headerName} é obrigatório`}
        arrow
        placement="top"
      >
        <i
          className="ph ph-warning"
          style={{ fontSize: 26, color: "#e53935" }}
        />
      </Tooltip>
    ) : (
      params.formattedValue
    );

    return (
      <Box
        sx={{
          width: '100%',
          textAlign: cenarioErro ? 'center' : 'left',
        }}
      >
        {content}
      </Box>
    );
  };

  switch (activeStep) {
    case 1:
      return (
        <>
          <Grid container spacing={2} sx={{ pl: 2, mt: 3 }}>
            <DataGrid
              rows={dadosCsv || []}
              columns={colunasProdutos}
              rowCount={dadosCsv?.length || 0}
              disableAcoes
            />
          </Grid>
          <InformacoesPessoasDialog 
            open={openInformacoesDialog}
            setOpen={setOpenInformacoesDialog}
            dadosPessoa={pessoaSelecionada}
            linhaSelecionada={linhaSelecionada}
            abaValue={abaValue}
            setAbaValue={setAbaValue}
            estados={estados}
            inputErros={inputErros}
            setLoading={setLoading}
            enderecoService={enderecoService}
            cacheDto={cacheDto}
            setCacheDto={setCacheDto}
          />
        </>
      );
    default:
      return (
        <Box
          {...getRootProps()}
          sx={{
            width: "100%",
            height: "90%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            marginTop: "1em",
            border:
              isDragActive || arquivoCsv
                ? "2px dashed grey"
                : "2px solid transparent",
            borderRadius: 2,
            padding: 2,
            backgroundColor: isDragActive || arquivoCsv ? "#e3f2fd" : "#fafafa",
            ":hover": { backgroundColor: "#e3f2fd", border: "2px dashed grey" },
            cursor: "pointer",
          }}
          onClick={() => csvUpload.current.click()}
        >
          <input
            {...getInputProps()}
            type="file"
            style={{ display: "none" }}
            ref={csvUpload}
            onChange={(event) => handleCsvChange(event.target.files)}
          />
          {arquivoCsv && (
            <i
              className="mb-2 ph-bold ph-check-circle"
              style={{ fontSize: 50, color: "#43a047" }}
            />
          )}
          <h4 style={{ fontWeight: 800, margin: 0 }}>
            {arquivoCsv
              ? "Arquivo Selecionado!"
              : "Arraste e solte ou Clique aqui para importar arquivo CSV/XLS"}
          </h4>
          {arquivoCsv?.name && (
            <div className="mt-2">
              <Typography>{arquivoCsv.name}</Typography>
            </div>
          )}
        </Box>
      );
  }
};

export default ImportarPessoasStepper;
